/**
 * /superadmin/funnel — the platform owner's signup funnel.
 *
 * Maps to GET /api/v1/admin/funnel?from=&to=&group= (super_admin only —
 * the support role gets a 403 and sees the error state). For companies
 * that signed up in the range (internal workspaces excluded): how many
 * came back, set up company details, added people, used a module, are
 * still active, and are on trial / trial ended / paying — by signup week
 * or month (India time), by signup source, and per company (each row
 * links to the tenant 360). Above it, the marketing site's visitors for
 * the same dates from Cloudflare Web Analytics (web_traffic), so visits →
 * signups reads in one place.
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AlertCircle, Filter, Globe, RefreshCw, Search } from "lucide-react";
import { adminConsoleApi, toApiError } from "@/lib/api";
import type {
  FunnelCounts,
  FunnelGroup,
  FunnelQuery,
  FunnelResponse,
  FunnelWebTraffic,
} from "@/types/admin-console";
import {
  BILLING_LABEL,
  cohortLabel,
  defaultFunnelRange,
  fmtISTDate,
  FUNNEL_STEPS,
  lastActiveLabel,
  MODULE_LABEL,
  pct,
  STAGE_LABEL,
} from "@/lib/funnel-format";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authgated/superadmin/funnel")({
  component: FunnelRoute,
});

// The columns the cohort + source tables show, in funnel order.
const TABLE_COLUMNS: { key: keyof FunnelCounts; label: string }[] = [
  { key: "came_back", label: "Came Back" },
  { key: "company_setup", label: "Company Set Up" },
  { key: "added_people", label: "Added People" },
  { key: "used_module", label: "Used a Module" },
  { key: "active_30d", label: "Active (30 Days)" },
  { key: "paid", label: "Paying" },
];

const SOURCE_KIND_LABEL = {
  campaign: "Campaign",
  referrer: "Referral Site",
  direct: "Direct",
} as const;

function FunnelRoute() {
  const initial = useMemo(() => defaultFunnelRange(), []);
  const [draft, setDraft] = useState<{ from: string; to: string; group: FunnelGroup }>({
    ...initial,
    group: "week",
  });
  const [applied, setApplied] = useState<FunnelQuery>({ ...initial, group: "week" });

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["admin-funnel", applied],
    queryFn: ({ signal }) => adminConsoleApi.getFunnel(applied, signal),
    placeholderData: keepPreviousData,
    // Cloudflare is slow the first time it sees a range; the server keeps
    // fetching in the background, so ask again until the visits arrive.
    refetchInterval: (q) => (q.state.data?.web_traffic?.pending ? 4000 : false),
  });

  const rangeInvalid = !draft.from || !draft.to || draft.from > draft.to;

  return (
    <>
      <PageHeader
        title="Signup Funnel"
        description="How far the companies that signed up in a period got, where they came from, and who is still around. Internal workspaces are left out."
      />

      <Card className="mb-4">
        <CardContent className="grid gap-4 py-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="f-from">Signed Up From</Label>
            <Input
              id="f-from"
              type="date"
              value={draft.from}
              max={draft.to || undefined}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-to">to</Label>
            <Input
              id="f-to"
              type="date"
              value={draft.to}
              min={draft.from || undefined}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-group">Group By</Label>
            <Select
              value={draft.group}
              onValueChange={(v) => setDraft((d) => ({ ...d, group: v as FunnelGroup }))}
            >
              <SelectTrigger id="f-group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Signup Week</SelectItem>
                <SelectItem value="month">Signup Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={() => setApplied({ ...draft })} disabled={isFetching || rangeInvalid}>
              <Search className="h-4 w-4" />
              Show
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh"
              className="ml-auto"
            >
              <RefreshCw className={isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            </Button>
          </div>
          {rangeInvalid && (
            <p className="text-sm text-destructive sm:col-span-2 lg:col-span-4">
              Pick a start date on or before the end date.
            </p>
          )}
        </CardContent>
      </Card>

      {error ? (
        <div className="flex flex-col items-start gap-3">
          <Alert variant="destructive" className="max-w-lg">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Couldn&apos;t load the funnel</AlertTitle>
            <AlertDescription>{toApiError(error).message}</AlertDescription>
          </Alert>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      ) : isLoading || !data ? (
        <div className="space-y-4">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : data.totals.signed_up === 0 ? (
        <div className="space-y-4">
          <WebTrafficCard web={data.web_traffic} signups={0} />
          <Card>
            <CardContent className="p-6">
              <EmptyState
                icon={Filter}
                title="No Signups in This Period"
                description={`No company signed up between ${fmtISTDate(data.from + "T00:00:00+05:30")} and ${fmtISTDate(data.to + "T00:00:00+05:30")}. Try a wider range.`}
              />
            </CardContent>
          </Card>
        </div>
      ) : (
        <FunnelBody data={data} />
      )}
    </>
  );
}

function FunnelBody({ data }: { data: FunnelResponse }) {
  const t = data.totals;
  const now = new Date();
  return (
    <div className="space-y-4">
      <WebTrafficCard web={data.web_traffic} signups={t.signed_up} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Funnel</CardTitle>
            <CardDescription>
              {t.signed_up} {t.signed_up === 1 ? "company" : "companies"} signed up between{" "}
              {fmtISTDate(data.from + "T00:00:00+05:30")} and {fmtISTDate(data.to + "T00:00:00+05:30")}.
              Each step counts companies that reached it, whatever else they did.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {FUNNEL_STEPS.map((step) => {
                const n = t[step.key];
                const width = t.signed_up > 0 ? Math.max((n / t.signed_up) * 100, n > 0 ? 2 : 0) : 0;
                return (
                  <li key={step.key} title={step.help}>
                    <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                      <span className="font-medium">{step.label}</span>
                      <span className="tabular-nums text-muted-foreground">
                        {n} <span className="text-xs">({pct(n, t.signed_up)})</span>
                      </span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-muted" aria-hidden>
                      <div className="h-2.5 rounded-full bg-primary" style={{ width: `${width}%` }} />
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{step.help}</p>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Right Now</CardTitle>
            <CardDescription>Of the companies that signed up in this period.</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Stat label="Active in the Last 7 Days" value={t.active_7d} whole={t.signed_up} />
              <Stat label="Active in the Last 30 Days" value={t.active_30d} whole={t.signed_up} />
              <Stat label="On Trial" value={t.on_trial} whole={t.signed_up} />
              <Stat label="Trial Ended / Free" value={t.trial_ended} whole={t.signed_up} />
              <Stat label="Paying" value={t.paid} whole={t.signed_up} />
            </dl>
            <p className="mt-4 text-xs text-muted-foreground">
              Active means someone in the company signed in, kept a session going or changed something.
            </p>
          </CardContent>
        </Card>
      </div>

      <CountsTable
        title={data.group === "month" ? "By Signup Month" : "By Signup Week"}
        description="Weeks start on Monday, India time. Percentages are of that cohort's signups."
        firstHeader={data.group === "month" ? "Month" : "Week"}
        rows={data.cohorts.map((c) => ({
          key: c.start,
          label: <span className="whitespace-nowrap">{cohortLabel(c.start, data.group)}</span>,
          counts: c,
        }))}
      />

      <CountsTable
        title="By Signup Source"
        description="The campaign (utm_source) if the first visit had one, otherwise the site that linked to us, otherwise Direct. Self-reported by the browser — a guide, not proof."
        firstHeader="Source"
        rows={data.sources.map((s) => ({
          key: s.source,
          label: (
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{s.source === "direct" ? "Direct" : s.source}</span>
              {s.kind !== "direct" && (
                <Badge variant="outline" className="text-xs font-normal">
                  {SOURCE_KIND_LABEL[s.kind]}
                </Badge>
              )}
            </span>
          ),
          counts: s,
        }))}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Companies</CardTitle>
          <CardDescription>
            Newest first. Stage is the furthest funnel step the company reached.
            {data.companies_truncated &&
              ` Showing the newest ${data.companies.length}; the counts above include all of them.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Signed Up</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Stage Reached</TableHead>
                  <TableHead className="text-right">People</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead>Billing</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.companies.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">
                      <Link
                        to="/superadmin/tenants/$id"
                        params={{ id: String(c.id) }}
                        className="hover:text-primary hover:underline"
                      >
                        {c.name}
                      </Link>
                      {!c.is_active && (
                        <span className="ml-2 text-xs font-normal text-destructive">inactive</span>
                      )}
                      {c.modules_used.length > 0 && (
                        <p className="text-xs font-normal text-muted-foreground">
                          Uses {c.modules_used.map((m) => MODULE_LABEL[m] ?? m).join(", ")}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {fmtISTDate(c.signed_up_at)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.source === "direct" ? "Direct" : c.source}
                      {c.utm_campaign && (
                        <p className="text-xs text-muted-foreground">Campaign: {c.utm_campaign}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={c.stage === "paid" ? "default" : "secondary"} className="whitespace-nowrap">
                        {STAGE_LABEL[c.stage] ?? c.stage}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{c.employees}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {lastActiveLabel(c.last_active_at, now)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{BILLING_LABEL[c.billing]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Marketing-site visitors for the funnel's dates (Cloudflare Web
 * Analytics). Visits → signups is shown only when Cloudflare covered the
 * whole range — a shortened window would compare different periods.
 */
function WebTrafficCard({ web, signups }: { web: FunnelWebTraffic | undefined; signups: number }) {
  const header = (
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <Globe className="h-4 w-4 text-muted-foreground" />
        Website Visits
      </CardTitle>
      <CardDescription>
        Visitors to workright.in for the same dates, from Cloudflare Web Analytics. Bots are left out;
        Cloudflare samples traffic, so treat these as estimates.
      </CardDescription>
    </CardHeader>
  );
  if (!web || !web.configured) {
    return (
      <Card>
        {header}
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Not set up yet. Add CF_ANALYTICS_API_TOKEN (Account Analytics: Read), CF_ACCOUNT_ID and
            CF_WEB_ANALYTICS_SITE_TAG to the backend&apos;s environment to see visits here.
          </p>
        </CardContent>
      </Card>
    );
  }
  if (web.pending) {
    return (
      <Card>
        {header}
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Fetching visits from Cloudflare — the first look at a date range can take a few seconds…
          </p>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }
  if (web.error || !web.report) {
    return (
      <Card>
        {header}
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Couldn&apos;t load website visits</AlertTitle>
            <AlertDescription>{web.error ?? "No data came back."}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }
  const r = web.report;
  const sources = [
    ...(r.direct_visits > 0 ? [{ host: "Direct", visits: r.direct_visits }] : []),
    ...r.referrers,
  ].sort((a, b) => b.visits - a.visits);
  return (
    <Card>
      {header}
      <CardContent className="space-y-4">
        {r.clamped && (
          <p className="text-sm text-muted-foreground">
            Cloudflare answers at most 92 days at a time, so this shows{" "}
            {fmtISTDate(r.from + "T00:00:00+05:30")} to {fmtISTDate(r.to + "T00:00:00+05:30")} only.
          </p>
        )}
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
          <div className="rounded-md border border-border p-3">
            <dt className="text-xs text-muted-foreground">Visits</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums">{r.visits.toLocaleString("en-IN")}</dd>
          </div>
          <div className="rounded-md border border-border p-3">
            <dt className="text-xs text-muted-foreground">Page Views</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums">{r.page_views.toLocaleString("en-IN")}</dd>
          </div>
          <div className="col-span-2 rounded-md border border-border p-3 sm:col-span-1">
            <dt className="text-xs text-muted-foreground">Visits → Signups</dt>
            <dd className="mt-1 text-lg font-semibold tabular-nums">
              {r.clamped ? "—" : pct(signups, r.visits)}{" "}
              <span className="text-xs font-normal text-muted-foreground">
                {r.clamped ? "pick 92 days or less" : `${signups} of ${r.visits.toLocaleString("en-IN")}`}
              </span>
            </dd>
          </div>
        </dl>
        <div className="grid gap-4 md:grid-cols-2">
          <TopList
            title="Where Visitors Came From"
            empty="No visits in this period."
            rows={sources.map((s) => ({ key: s.host, label: s.host, value: s.visits, unit: "visits" }))}
          />
          <TopList
            title="Most Viewed Pages"
            empty="No page views in this period."
            rows={r.pages.map((p) => ({ key: p.path, label: p.path, value: p.page_views, unit: "views" }))}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function TopList({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: { key: string; label: string; value: number; unit: string }[];
}) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-medium">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border text-sm">
          {rows.map((row) => (
            <li key={row.key} className="flex items-baseline justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate" title={row.label}>
                {row.label}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {row.value.toLocaleString("en-IN")} <span className="text-xs">{row.unit}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Stat({ label, value, whole }: { label: string; value: number; whole: number }) {
  return (
    <div className="rounded-md border border-border p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums">
        {value} <span className="text-xs font-normal text-muted-foreground">{pct(value, whole)}</span>
      </dd>
    </div>
  );
}

function CountsTable({
  title,
  description,
  firstHeader,
  rows,
}: {
  title: string;
  description: string;
  firstHeader: string;
  rows: { key: string; label: React.ReactNode; counts: FunnelCounts }[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{firstHeader}</TableHead>
                <TableHead className="text-right">Signed Up</TableHead>
                {TABLE_COLUMNS.map((c) => (
                  <TableHead key={c.key} className="whitespace-nowrap text-right">
                    {c.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell className="text-sm">{r.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.counts.signed_up}</TableCell>
                  {TABLE_COLUMNS.map((c) => (
                    <TableCell key={c.key} className="whitespace-nowrap text-right tabular-nums">
                      {r.counts.signed_up === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <>
                          {r.counts[c.key]}{" "}
                          <span className="text-xs text-muted-foreground">
                            {pct(r.counts[c.key], r.counts.signed_up)}
                          </span>
                        </>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
